import type { HTMLAttributes } from 'react';
import React from 'react';
import classNames from 'classnames';
import { useAppContext } from '$util/AppContext';
import type { AppContextGlobal } from '$util/AppContextGlobal';
import { Row, Col } from '$src-components/atoms/Grid';
import { NavigationMenu } from '$quantum/molecules/NavigationMenu';
import { LogoPicture } from '$src-components/atoms/LogoPicture';
import { getCmsData } from './cms';

import styles from './index.module.scss';

export interface TopNavigationBarProps extends HTMLAttributes<HTMLElement> {
  readonly showLoginLink?: boolean;
  readonly navigationMenuFooter?: React.ReactNode;
}

/**
 * The base component for a navigation bar that needs logo, menu and login link.
 * Extend this component for different flows.
 * eg. `templates/LandingTopNavigationBar`.
 *
 * @deprecated use from `src`
 */
export function TopNavigationBar({
  showLoginLink = true,
  navigationMenuFooter,
  children,
  className,
  ...props
}: TopNavigationBarProps): JSX.Element {
  const {
    cmsContent: { dictionary },
  } = useAppContext<AppContextGlobal>();
  const { loginLink } = getCmsData(dictionary);

  const LoginLink = (
    <a className={classNames('login', styles.login)} href={loginLink.url}>
      {loginLink.text}
    </a>
  );

  return (
    <nav className={classNames(TopNavigationBar.displayName, styles.component, className)} {...props}>
      <Row className={styles.Row}>
        <Col className={styles.Col}>
          <LogoPicture url="/" />
          <NavigationMenu footer={navigationMenuFooter ?? LoginLink} />

          {/**
           * Things contextual to the page, we should avoid hardcoding page logic inside this component.
           * Instead create specialized components that compose components based on the page.
           * eg. `templates/LandingTopNavigationBar`.
           */}
          {children}

          {showLoginLink && LoginLink}
        </Col>
      </Row>
    </nav>
  );
}
TopNavigationBar.displayName = 'TopNavigationBar';
